<template>
  <StepCard
    step-id="invite_members"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('send_invites')"
    :save-hint
    @save="save"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <ZoneTable
        :columns="summaryColumns"
        :rows="data.invites"
        :toolbar="false"
        :paginated="false"
        sticky-header
        :label="t('step_names.invite_members')"
        data-testid="invite-summary"
      />
    </template>

    <form
      class="flex flex-col gap-2"
      novalidate
      @submit="addInvite"
    >
      <ZoneLabel
        :html-for="emailId"
        :label="t('email_address')"
        :invalid="!!errors.email"
      />
      <div
        class="flex items-stretch rounded-md border bg-white focus-within:ring-1"
        :class="errors.email ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500' : 'border-gray-300 focus-within:border-blue-600 focus-within:ring-blue-600'"
      >
        <input
          :id="emailId"
          v-model="email"
          type="text"
          inputmode="email"
          autocomplete="off"
          class="min-w-0 flex-1 rounded-l-md border-0 bg-transparent px-4 text-gray-700 placeholder:font-regular placeholder:text-gray-400 focus:ring-0"
          :placeholder="t('email_placeholder')"
          :aria-invalid="!!errors.email"
          data-testid="invite-email"
          @blur="validateTypedEmail"
        />
        <span
          class="my-2 w-px shrink-0 bg-gray-300"
          aria-hidden="true"
        />
        <ZoneFloatingMenu
          :selected="role"
          align="end"
          :align-offset="-33"
          width="25.6rem"
          :items="roleOptions"
          :aria-label="t('role')"
          data-testid="invite-role"
          @select="role = $event.id"
        >
          <template #trigger>
            <button
              type="button"
              class="relative w-32 shrink-0 py-2.5 pl-3 pr-8 text-left hover:bg-gray-50"
            >
              <span class="block truncate text-sm text-gray-500">{{ t(`roles.${role}.name`) }}</span>
              <ZoneIcon
                icon="expand_more"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              />
            </button>
          </template>
        </ZoneFloatingMenu>
        <ZoneButton
          reset
          :disabled="!email || !meta.valid"
          :tooltip="email && meta.valid ? null : t(email ? 'fix_errors' : 'add_hint')"
          :aria-label="t('add')"
          class="flex h-full shrink-0 cursor-pointer items-center py-2.5 pl-1 pr-3 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          data-testid="invite-add"
          @click="addInvite"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="size-4"
            aria-hidden="true"
          ><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>
        </ZoneButton>
      </div>
      <p
        v-if="errors.email"
        class="text-xs text-red-600"
        role="alert"
        data-testid="invite-error"
      >
        {{ errors.email }}
      </p>
      <p class="flex items-center gap-1.5 text-xs text-gray-500">
        {{ t('press') }}
        <kbd class="inline-flex items-center gap-1 rounded border font-mono border-gray-300 bg-gray-50 px-1.5 py-0.5 font-medium text-gray-600">{{ t('enter') }} <span aria-hidden="true">↵</span></kbd>
        {{ t('to_add') }}
      </p>
    </form>

    <ul
      v-if="queue.length > 0"
      class="mt-3 flex flex-col gap-2"
      data-testid="invite-queue"
    >
      <li
        v-for="invite in queue"
        :key="invite.email"
        class="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
      >
        <ZoneIcon
          icon="mail"
          class="text-gray-400"
          aria-hidden="true"
        />
        <span class="flex-1 truncate text-sm text-gray-700">{{ invite.email }}</span>
        <ZonePill>{{ roleName(invite.role) }}</ZonePill>
        <ZoneIconButton
          icon="close"
          size="sm"
          variant="ghost"
          color="gray"
          :aria-label="t('remove', { email: invite.email })"
          :data-testid="`invite-remove-${invite.email}`"
          @click="removeInvite(invite.email)"
        />
      </li>
    </ul>

    <div class="mt-4 border-t border-gray-200 pt-3">
      <p class="mb-2 text-xs font-medium uppercase text-gray-600">{{ t('share_link') }}</p>
      <div class="flex items-center gap-2 rounded-md border border-gray-200 bg-white py-1.5 pl-3 pr-2">
        <span
          class="flex-1 truncate text-sm font-medium text-gray-700"
          data-testid="invite-link"
        >{{ data.invite_url }}</span>
        <ZoneButton
          reset
          class="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700"
          :class="copied ? 'text-green-500' : 'text-blue-700 hover:underline'"
          data-testid="invite-copy"
          @click="copyLink"
        >
          <ZoneIcon
            :icon="copied ? 'check' : 'content_copy'"
            class="text-base!"
            aria-hidden="true"
          />
          {{ copied ? t('copied') : t('copy') }}
        </ZoneButton>
      </div>
      <p class="mt-1.5 text-xs text-gray-400">{{ t('link_hint') }}</p>
    </div>
  </StepCard>
</template>

<script setup>
import { useForm } from 'vee-validate';
import { computed, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { InvitesResource } from '~/resources';
import { SUMMARY_COLUMN, SUMMARY_VALUE_COLUMN } from '~/components/experiments/0239_getting_started_checklist/summaryTable';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneFloatingMenu from 'zone/components/base/FloatingMenu.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZoneIconButton from 'zone/components/base/IconButton.vue';
import ZoneLabel from 'zone/components/base/Label.vue';
import ZonePill from 'zone/components/base/Pill.vue';
import ZoneTable from 'zone/components/base/Table.vue';
import { chain, email as emailRule, required } from '~/zone_inertia/validations';

const ROLES = { user: 0, manager: 2 };
const MAX_INVITES = 20;

const { data, completed, organization } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, serverMessages, finishStep, run } = useStepSave(emit);

const emailId = useId();
const role = ref('user');
const queue = ref([]);
const copied = ref(false);

const save = () => run(async () => {
  const response = await InvitesResource.bulkCreate(
    { id: organization.id },
    { invite: queue.value, source: 'Getting Started Checklist' },
  );

  return response.success ? finishStep(organization.id, 'invite_members') : serverMessages(response);
});

const { defineField, errors, meta, handleSubmit, resetForm, validateField } = useForm({
  initialValues: { email: '' },
  validationSchema: {
    email: chain([
      required(),
      emailRule(),
      (value) => !queue.value.some((invite) => sameEmail(invite, value)) || t('duplicate', { email: value.trim() }),
      (value) => !data.invites.some((invite) => sameEmail(invite, value)) || t('already_invited', { email: value.trim() }),
      () => queue.value.length < MAX_INVITES || t('max_invites', { count: MAX_INVITES }),
    ]),
  },
});
const [email] = defineField('email', ({ errors: fieldErrors }) => ({ validateOnModelUpdate: fieldErrors.length > 0 }));
const validateTypedEmail = () => email.value && validateField('email');
const sameEmail = (invite, value) => invite.email.toLowerCase() === value.trim().toLowerCase();
const saveHint = computed(() => (queue.value.length === 0 ? t('save_hint') : null));
const roleOptions = computed(() => Object.keys(ROLES).map((id) => ({
  id,
  label: t(`roles.${id}.name`),
  description: t(`roles.${id}.description`),
})));
const summaryColumns = computed(() => [
  { key: 'email', label: t('email'), ...SUMMARY_COLUMN },
  { key: 'role', label: t('role'), formatter: roleName, ...SUMMARY_VALUE_COLUMN, width: 'w-1/3' },
]);

const roleName = (value) => {
  const id = Object.keys(ROLES).find((key) => ROLES[key] === value || key === value);

  return id ? t(`roles.${id}.name`) : '';
};

const addInvite = handleSubmit((values) => {
  queue.value = [...queue.value, { email: values.email.trim(), role: ROLES[role.value] }];
  resetForm();
});

const removeInvite = (address) => {
  queue.value = queue.value.filter((invite) => invite.email !== address);
  validateTypedEmail();
};

const copyLink = async () => {
  await navigator.clipboard.writeText(data.invite_url);
  copied.value = true;
};
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Hubstaff works best when your whole team is in it.",
    "email_address": "Email address",
    "email_placeholder": "name{'@'}company.com",
    "roles": {
      "user": { "name": "User", "description": "Tracks time, sees their own data" },
      "manager": { "name": "Manager", "description": "Manages projects and members" }
    },
    "role": "Role",
    "email": "Email",
    "remove": "Remove {email}",
    "add": "Add",
    "add_hint": "Type an email address first.",
    "save_hint": "Add at least one email address first.",
    "duplicate": "{email} is already in your list.",
    "already_invited": "{email} is already invited.",
    "max_invites": "You can send up to {count} invites at a time.",
    "share_link": "Or share an invite link",
    "copy": "Copy",
    "copied": "Copied",
    "link_hint": "Anyone with the link can join as a User.",
    "send_invites": "Send invites",
    "edit": "Invite more members"
  }
}
</i18n>
